import { useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';
import TradeInSlider from '@/components/tradein/TradeInSlider';
import i18n from '@/settings/i18n';
import { isTradeInAvailable } from '@/lib/trade-in';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { isInternalUser } from '@/lib/internal-user';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import TradeInQuoteCard from './TradeInQuoteCard';
import useTradeInQuote from './useTradeInQuote';

const t = i18n.init('buyback');

const TradeInArtwork = () => (
  <div className="relative w-24 shrink-0 self-stretch">
    <Image
      src={getEdgeUrl('images/buyback/pdp/banner-1.png')}
      alt=""
      aria-hidden="true"
      fill
      quality={100}
      sizes="96px"
      className="object-cover object-left"
    />
  </div>
);

const TradeInBanner = ({
  className,
  showHeading = true,
}: {
  className?: string;
  showHeading?: boolean;
}) => {
  const [showTradeIn, setShowTradeIn] = useState(false);
  const { quote, clear } = useTradeInQuote();
  // Still an internal trial: the widget hands back a quote the shopper is
  // never charged against, so signed-out and external shoppers get no entry
  // point into it at all - banner, widget and quote card alike.
  const { customer } = useCustomerContext();

  if (!isTradeInAvailable()) {
    return null;
  }

  // Once the widget has told us what was traded, the prompt to trade has done
  // its job and the section shows the trade-in itself.
  if (quote && isInternalUser(customer?.email)) {
    return (
      <TradeInQuoteCard
        quote={quote}
        onRemove={clear}
        className={className}
        showHeading={showHeading}
      />
    );
  }

  return (
    <div className={cn('mb-2', className)}>
      {showHeading && <h2 className="mb-2 text-sm font-bold">{t`Trade-in`}</h2>}
      <div className="relative flex min-h-[95px] w-full items-center gap-4 overflow-hidden rounded-lg border border-[#E5E7EB] bg-[linear-gradient(90deg,_#ECFCF5_0%,_#FFFFFF_100%)] pr-5 text-xs text-gray-700">
        <TradeInArtwork />
        <div className="flex flex-1 flex-col flex-wrap justify-between gap-4 py-4 xs:flex-row xs:items-center">
          <div className="flex-1">
            <h3 className="font-bold">{t`Trade in your old tech today`}</h3>
            <p>{t`and save big on your new purchase!`}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowTradeIn(true)}
            className="shrink-0 rounded-md border border-gray-700 px-4 py-2 text-center text-xs text-gray-700 transition duration-150 ease-in-out hover:bg-gray-700 hover:text-white focus:outline-none"
          >
            <span className="xs:hidden">{t`Start Now`}</span>
            <span className="hidden xs:inline">{t`Start trade-in`}</span>
          </button>
        </div>
      </div>
      <TradeInSlider showModal={showTradeIn} setShowModal={setShowTradeIn} />
    </div>
  );
};

export default TradeInBanner;
